import React from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useIsPresent, useReducedMotion } from 'motion/react';

import { isSpookySeason } from './spooky-launch';

const SIZE = 168;
// Spokes fan from the corner between the right edge (0) and the top edge (90).
// The two edge spokes are anchors only: rings tie off on them, so every ring
// spans the whole corner, but they are not drawn over the page's own edges.
const SPOKES = [0, 10, 21, 33, 45, 57, 69, 80, 90];
// Tighter near the hub and wider toward the rim, like an orb weaver's spiral.
const RINGS = [10, 19, 29, 40, 52, 65, 79, 94, 110, 127, 145];

const point = (radius: number, angle: number) => {
  const rad = (angle * Math.PI) / 180;
  return { x: SIZE - radius * Math.sin(rad), y: radius * Math.cos(rad) };
};

// A fixed per-crossing wobble, so the web looks hand-spun but renders the same
// on every visit.
const wobble = (ring: number, spoke: number) =>
  1 + 0.05 * Math.sin(ring * 12.9898 + spoke * 78.233);

const fmt = (p: { x: number; y: number }) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`;

// Each ring segment sags a little toward the corner, the way silk does under
// its own weight.
const WEB_PATH = [
  ...SPOKES.slice(1, -1).map((angle) => `M${SIZE} 0L${fmt(point(SIZE * 0.97, angle))}`),
  ...RINGS.map((radius, r) =>
    SPOKES.slice(1)
      .map((angle, i) => {
        const prev = SPOKES[i];
        const from = point(radius * wobble(r, i), prev);
        const to = point(radius * wobble(r, i + 1), angle);
        const sag = point(radius * 0.88, (prev + angle) / 2);
        return `M${fmt(from)}Q${fmt(sag)} ${fmt(to)}`;
      })
      .join('')
  )
].join('');

const SPIDER_W = 52;
const SPIDER_H = 42;
// The spider walks the 45-degree spoke and stops this far out from the hub.
const PERCH = 120;
const PERCH_OFFSET = PERCH / Math.SQRT2;
// How far past the corner, along the same spoke, the spider waits off the page.
// The sign hangs about 76px below the spider and spreads 64px either side of
// it, so this keeps the whole group out of view before and after the walk.
const OFFSTAGE = 100;
// Translations for the spider's box, whose resting place is the corner itself.
const TUCKED = { x: SPIDER_W / 2 + OFFSTAGE, y: -SPIDER_H / 2 - OFFSTAGE };
const PERCHED = { x: SPIDER_W / 2 - PERCH_OFFSET, y: PERCH_OFFSET - SPIDER_H / 2 };
// Enough room for the web, the perched spider and its sign; everything is
// clipped to it so the spider waiting off the page never widens it.
const STAGE_W = 240;
const STAGE_H = 240;

const GREET_MS = 6600;
const CRAWL_DELAY_S = 0.6;

// The greeting plays once per page load, not on every return to home. Both of
// StrictMode's back-to-back renders land inside the window, so dev still shows it.
let greetedAt: number | undefined;
const shouldGreet = () => {
  greetedAt ??= Date.now();
  return Date.now() - greetedAt < 1000;
};

// Each leg is its own stroke so it can step from its own hip. The lowest pair
// are arms: they hold the sign's threads, so they stay still while it walks.
const LEGS: { d: string; hip: [number, number]; arm?: true }[] = [
  { d: 'M24 21Q13 8 5 13', hip: [24, 21] },
  { d: 'M23 24Q10 18 2 25', hip: [23, 24] },
  { d: 'M23 28Q10 31 4 39', hip: [23, 28] },
  { d: 'M25 31Q17 40 12 46', hip: [25, 31], arm: true },
  { d: 'M36 21Q47 8 55 13', hip: [36, 21] },
  { d: 'M37 24Q50 18 58 25', hip: [37, 24] },
  { d: 'M37 28Q50 31 56 39', hip: [37, 28] },
  { d: 'M35 31Q43 40 48 46', hip: [35, 31], arm: true }
];
// Where the arms end, in px within the spider's box: the threads tie on here.
const ARM_SPAN = ((48 - 12) * SPIDER_W) / 60;
const ARM_Y = (46 * SPIDER_H) / 48;
const STEP_S = 0.22;

// A CSS animation rather than motion, so each leg pivots at its hip in the
// SVG's own coordinates without motion's bounding-box origin for SVG.
const LEG_KEYFRAMES =
  '@keyframes spooky-spider-step{from{transform:rotate(-16deg)}to{transform:rotate(16deg)}}';

/**
 * While walking, alternate legs step in opposite beats on each side and the
 * two sides mirror each other, so it scuttles rather than flaps. The arms keep
 * still so the sign stays in hand.
 */
const FriendlySpider = (props: { walking: boolean }) => (
  <svg
    viewBox="0 0 60 48"
    width={SPIDER_W}
    height={SPIDER_H}
    className="overflow-visible text-neutral-800 dark:text-neutral-500"
  >
    <style>{LEG_KEYFRAMES}</style>
    {LEGS.map((leg, i) => (
      <path
        key={leg.d}
        d={leg.d}
        fill="none"
        stroke="currentColor"
        strokeWidth={2.4}
        strokeLinecap="round"
        style={{
          transformBox: 'view-box',
          transformOrigin: `${leg.hip[0]}px ${leg.hip[1]}px`,
          animation:
            props.walking && !leg.arm
              ? `spooky-spider-step ${STEP_S}s ease-in-out ${((i + Math.floor(i / 4)) % 2) * -STEP_S}s infinite alternate`
              : undefined
        }}
      />
    ))}
    <circle cx={30} cy={26} r={11} fill="currentColor" />
    <circle cx={25.8} cy={23} r={3.8} fill="white" />
    <circle cx={34.2} cy={23} r={3.8} fill="white" />
    <circle cx={26.3} cy={23.4} r={1.8} className="fill-neutral-900" />
    <circle cx={34.7} cy={23.4} r={1.8} className="fill-neutral-900" />
    {/* A glint in each eye for a bright, friendly look. */}
    <circle cx={27} cy={22.7} r={0.6} fill="white" />
    <circle cx={35.4} cy={22.7} r={0.6} fill="white" />
    <circle cx={22.4} cy={29.2} r={1.7} className="fill-pink-300/90" />
    <circle cx={37.6} cy={29.2} r={1.7} className="fill-pink-300/90" />
    {/* A crescent grin: a filled shape tapering to points, so it reads as a
        smooth smile at this size rather than a thick stroke. */}
    <path d="M26 29.4C27.4 33.4 32.6 33.4 34 29.4C32 31.4 28 31.4 26 29.4Z" fill="white" />
  </svg>
);

/**
 * Crawls out of the corner along the web, holding its sign, then crawls back
 * in. It walks while it is arriving or leaving and rests in between.
 */
const CrawlingSpider = () => {
  const present = useIsPresent();
  const [arrived, setArrived] = React.useState(false);
  const walking = !arrived || !present;

  return (
    <motion.div
      className="absolute right-0 top-0"
      style={{ width: SPIDER_W, height: SPIDER_H }}
      initial={TUCKED}
      animate={{
        ...PERCHED,
        transition: { delay: CRAWL_DELAY_S, duration: 2.2, ease: [0.25, 0.6, 0.4, 1] }
      }}
      exit={{ ...TUCKED, transition: { duration: 1.8, ease: [0.5, 0, 0.75, 0.6] } }}
      onAnimationComplete={() => setArrived(true)}
    >
      {/* A little bob with each step while walking, carrying the sign with it
          so the arms and threads never come apart. */}
      <motion.div
        className="relative"
        animate={{ y: walking ? [0, -1.5, 0] : 0 }}
        transition={walking ? { duration: STEP_S * 2, repeat: Infinity } : { duration: 0.2 }}
      >
        <FriendlySpider walking={walking} />
        {/* The sign hangs from the arms' tips on two short threads and swings
            gently. Centering lives on a plain wrapper because motion owns the
            inner element's transform. */}
        <div className="absolute left-1/2 -translate-x-1/2" style={{ top: ARM_Y }}>
          <motion.div
            className="flex flex-col items-center"
            style={{ transformOrigin: '50% 0%' }}
            animate={{ rotate: [-3, 3, -3] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="flex justify-between" style={{ width: ARM_SPAN + 1 }}>
              <div className="h-2.5 w-px bg-neutral-400/70 dark:bg-neutral-500/70" />
              <div className="h-2.5 w-px bg-neutral-400/70 dark:bg-neutral-500/70" />
            </div>
            <div className="whitespace-nowrap rounded-md border border-orange-200 bg-white px-2.5 py-1 text-xs font-semibold text-orange-600 shadow-sm dark:border-orange-900 dark:bg-neutral-800 dark:text-orange-400">
              Happy Halloween!
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/**
 * A faint cobweb in the page's top-right corner during spooky season, with a
 * spider that crawls out to say hello once per page load, and again whenever
 * the web is clicked. Only from `lg` up, where the app has no top bar to
 * collide with; portaled to the body so it sits in the document's own corner
 * and scrolls away with the page.
 */
export const SpookyCobweb = () => {
  const [inSeason] = React.useState(() => isSpookySeason());
  const stillness = useReducedMotion();
  const [greeting, setGreeting] = React.useState(shouldGreet);
  React.useEffect(() => {
    if (!greeting) {
      return;
    }
    const timer = window.setTimeout(() => setGreeting(false), GREET_MS);
    return () => window.clearTimeout(timer);
  }, [greeting]);

  if (!inSeason || typeof document === 'undefined') {
    return null;
  }
  return createPortal(
    <div
      aria-hidden="true"
      className="pointer-events-none absolute right-0 top-0 hidden overflow-hidden lg:block"
      style={{ width: STAGE_W, height: STAGE_H }}
    >
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        fill="none"
        strokeLinecap="round"
        className="absolute right-0 top-0 stroke-neutral-400/40 dark:stroke-neutral-500/40"
      >
        <path d={WEB_PATH} strokeWidth={0.75} />
        {/* The silk is too fine to click, so the web's whole footprint is the
            target. A click mid-greeting is ignored rather than restarting it. */}
        {!stillness && (
          <path
            d={`M${SIZE} 0V${SIZE}A${SIZE} ${SIZE} 0 0 1 0 0Z`}
            fill="transparent"
            stroke="none"
            className="pointer-events-auto cursor-pointer"
            onClick={() => setGreeting(true)}
          />
        )}
      </svg>
      <AnimatePresence>{!stillness && greeting && <CrawlingSpider key="spider" />}</AnimatePresence>
    </div>,
    document.body
  );
};
